import { SVP_0_1, type BalanceProfile } from '../profile.js';
import { DEFAULT_EXECUTION_LIMITS, type ExecutionLimits } from '../sim/execution-limits.js';

/** A rarity tier id; absent means the tier a skill with no rarity is judged under (`DEFAULT_CAST_REACH_RARITY`). */
export type Rarity = string;

/**
 * Everything a verdict on one compiled skill depends on besides the skill: the balance profile it is judged
 * against, its rarity tier, and the limits of the host that will run it. Two judgements with an equal
 * context give the same answer, so a verdict is remembered per context (`legalityContextKey`).
 */
export type LegalityContext = { profile: BalanceProfile; rarity: Rarity | undefined; hostLimits: ExecutionLimits };

/**
 * The context of a host that states `limits` (a runtime's `executionLimits`). A limit it does not state narrows nothing: the
 * cast's own attack cap and its rarity's generations apply alone, as they do in the runtime, which falls back to them.
 */
export function hostContext(profile: BalanceProfile, rarity: Rarity | undefined, limits: Partial<ExecutionLimits> | undefined): LegalityContext {
  return {
    profile, rarity,
    hostLimits: {
      maxEvents: limits?.maxEvents ?? DEFAULT_EXECUTION_LIMITS.maxEvents,
      maxSpawnedAttacks: limits?.maxSpawnedAttacks ?? Infinity,
      maxProcGeneration: limits?.maxProcGeneration ?? Infinity,
    },
  };
}

/** The context of a call that states no host limit but the attacks one cast may spawn: the host narrows nothing else. */
export function unlimitedHostContext(profile: BalanceProfile, rarity: Rarity | undefined, attackLimit: number | undefined): LegalityContext {
  return hostContext(profile, rarity, attackLimit === undefined ? undefined : { maxSpawnedAttacks: attackLimit });
}

/** The part of a context that is data (the profile is told apart by object identity, where the verdicts are kept). */
export function legalityContextKey(context: LegalityContext): string {
  const { maxEvents, maxSpawnedAttacks, maxProcGeneration } = context.hostLimits;
  return `${context.rarity ?? ''}|${maxEvents}|${maxSpawnedAttacks}|${maxProcGeneration}`;
}

/**
 * The context a stored skill is judged in when it is loaded and owned: the default profile, no rarity (skills carry
 * none), and no host limit beyond the default execution limits. Built per call: the default execution limits belong to a
 * module that may still be loading when this one is.
 */
export function persistedSkillContext(): LegalityContext {
  return unlimitedHostContext(SVP_0_1, undefined, undefined);
}
